import { cn, getGradientPair } from '../lib/utils'
import { splitPanelStyle } from '../lib/splitPanel'
import { focalPosition, type FocalAnchor } from '../lib/focalPosition'

/**
 * Where the image box sits relative to the card, as fractions of the card's
 * height. Lets a card that shows only a slice of something (a partial set)
 * paint the image as if it covered the whole thing, so it lines up with a
 * full-size layer drawn behind it.
 */
export interface ImageFrame {
  top: number
  height: number
}

/** Absolutely positioned image / b2b split / seed-gradient layer for a card. */
export function CardBackground({
  imageUrl,
  imageFocal,
  imageUrls,
  imageFocals,
  seed,
  imageOverlay,
  gradientOverlay,
  frame,
  className,
}: {
  imageUrl?: string
  imageFocal?: FocalAnchor
  imageUrls?: string[]
  imageFocals?: FocalAnchor[]
  seed?: string
  imageOverlay: number
  gradientOverlay: number
  frame?: ImageFrame
  className?: string
}) {
  const multi = imageUrls && imageUrls.length > 1 ? imageUrls : null
  const withOverlay = (url: string) =>
    `linear-gradient(rgba(0,0,0,${imageOverlay}), rgba(0,0,0,${imageOverlay})), url(${url})`

  let single: React.CSSProperties | null = null
  if (!multi && imageUrl) {
    single = { backgroundImage: withOverlay(imageUrl), backgroundSize: 'cover', backgroundPosition: focalPosition(imageFocal, 'center top') }
  } else if (!multi && seed) {
    const [c1, c2] = getGradientPair(seed)
    single = {
      backgroundImage: `linear-gradient(rgba(0,0,0,${gradientOverlay}), rgba(0,0,0,${gradientOverlay})), linear-gradient(135deg, ${c1}, ${c2})`,
    }
  }

  return (
    <div className={cn('absolute inset-0 z-0 overflow-hidden rounded-[inherit]', className)}>
      <div
        className="absolute inset-x-0"
        style={frame ? { top: `${frame.top * 100}%`, height: `${frame.height * 100}%` } : { top: 0, bottom: 0 }}
      >
        {multi
          ? multi.map((url, i) => (
              <div
                key={i}
                className="absolute inset-y-0"
                style={{
                  ...splitPanelStyle(i, multi.length),
                  backgroundImage: withOverlay(url),
                  backgroundSize: 'cover',
                  backgroundPosition: focalPosition(imageFocals?.[i], 'center top'),
                }}
              />
            ))
          : single && <div className="absolute inset-0" style={single} />}
      </div>
    </div>
  )
}
